// Global
import { RouterEvent, useRouter } from 'next/router';
import { useEffect, useRef } from 'react';

export const useOnRouteChange = (
  callback: () => void,
  events: RouterEvent[] = ['routeChangeComplete'],
  runOnHashChange = false
) => {
  const router = useRouter();
  const previousPathRef = useRef<string | null>(null);

  // Ensures that we don't needless attach and detach events due to depencency changing
  const eventString = events.join('|');
  useEffect(() => {
    const events = eventString.length > 0 ? (eventString.split('|') as RouterEvent[]) : [];
    events.forEach((eventName) => router.events.on(eventName, callback));

    return () => {
      events.forEach((eventName) => router.events.off(eventName, callback));
    };
  }, [callback, eventString, router.events]);

  useEffect(() => {
    if (runOnHashChange) {
      window.addEventListener('hashchange', callback);
    }

    return () => {
      if (runOnHashChange) {
        window.removeEventListener('hashchange', callback);
      }
    };
  }, [callback, runOnHashChange]);

  useEffect(() => {
    if (previousPathRef.current === null) {
      previousPathRef.current = router.asPath;
      return;
    }

    if (previousPathRef.current !== router.asPath) {
      previousPathRef.current = router.asPath;
      callback();
    }
  }, [callback, router.asPath]);
};

export function useOnHashChange(callback: () => void) {
  return useOnRouteChange(callback, ['routeChangeComplete'], true);
}
